import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHandler } from '@framework/hooks/useHandler';
import { pointer, select } from 'd3-selection';
import type { ZoomBehavior } from 'd3-zoom';
import { zoom as Zoom } from 'd3-zoom';
import { easeQuadOut } from 'd3-ease';
import { MouseEventButton } from '@view-model/models/common/basic/MouseEventButton';
import type { UserOperationModes } from '@user/pages/ViewModelPage/UserOperationModesProvider';
import { zoomIdentity, ZoomTransform } from 'd3';
import type { Point } from '@view-model/models/common/basic';
import type { ScaleExtent } from '../domain/ScaleExtent';
import type { ViewBox } from '@view-model/domain/view-model';
import { getDefaultStore } from 'jotai';
import { followingUserIdAtom } from '@view-model/Viewer/followingUserAtom';

type Props = {
  zoomPanHandlerRef: React.RefObject<SVGGElement | null>;
  userOperationMode: UserOperationModes;
  scaleExtent: ScaleExtent;
  viewBox: ViewBox;
  onZoom: (t: ZoomTransform) => void;
  enabled?: boolean;
};

type Extent = [[number, number], [number, number]];

interface WheelEventEx extends WheelEvent {
  readonly wheelDeltaX: number;
  readonly wheelDeltaY: number;
}

interface EventTargetEx extends EventTarget {
  readonly __zoom: ZoomTransform;
}

const programmaticZoomEvent = new Event('programmatic-zoom');

const translateExtent = [
  [-Infinity, -Infinity],
  [Infinity, Infinity],
];

const getExtent = (viewBox: ViewBox): Extent => {
  const { minX: x, minY: y, width, height } = viewBox;

  return [
    [x, y],
    [x + width, y + height],
  ];
};

const scaled = (transform: ZoomTransform, k: number, scaleExtent: ScaleExtent): ZoomTransform => {
  const k2 = Math.max(scaleExtent.min, Math.min(scaleExtent.max, k));
  return k2 === transform.k ? transform : new ZoomTransform(k2, transform.x, transform.y);
};

const translate = (transform: ZoomTransform, p0: number[], p1: number[]): ZoomTransform => {
  const x = p0[0] - p1[0] * transform.k;
  const y = p0[1] - p1[1] * transform.k;

  return x === transform.x && y === transform.y ? transform : new ZoomTransform(transform.k, x, y);
};

const constrain = (transform: ZoomTransform, extent: Extent): ZoomTransform => {
  const dx0 = transform.invertX(extent[0][0]) - translateExtent[0][0];
  const dx1 = transform.invertX(extent[1][0]) - translateExtent[1][0];
  const dy0 = transform.invertY(extent[0][1]) - translateExtent[0][1];
  const dy1 = transform.invertY(extent[1][1]) - translateExtent[1][1];

  return transform.translate(
    dx1 > dx0 ? (dx0 + dx1) / 2 : Math.min(0, dx0) || Math.max(0, dx1),
    dy1 > dy0 ? (dy0 + dy1) / 2 : Math.min(0, dy0) || Math.max(0, dy1)
  );
};

export const useZoomPanHandler = (props: Props) => {
  const { zoomPanHandlerRef, userOperationMode, scaleExtent, viewBox, onZoom, enabled = true } = props;
  const onZoomHandler = useHandler(onZoom);
  const isEnabled = useHandler(() => enabled);
  const animationFrameRef = useRef<number | null>(null);
  const pendingTransformRef = useRef<ZoomTransform | null>(null);

  const cancelPendingZoom = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    pendingTransformRef.current = null;
  }, []);

  useEffect(() => cancelPendingZoom, [cancelPendingZoom]);

  const handleZoom = useCallback(
    (transform: ZoomTransform, sourceEvent: Event | null) => {
      if (!isEnabled()) {
        cancelPendingZoom();
        return;
      }
      if (!sourceEvent || sourceEvent === programmaticZoomEvent) {
        cancelPendingZoom();
        onZoomHandler(transform);
        return;
      }

      // 連続入力の各イベントで React を更新すると、描画後の更新が完了する前に次の描画が始まる。
      pendingTransformRef.current = transform;
      if (animationFrameRef.current !== null) return;
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = null;
        const pendingTransform = pendingTransformRef.current;
        pendingTransformRef.current = null;
        if (pendingTransform && isEnabled()) onZoomHandler(pendingTransform);
      });
    },
    [cancelPendingZoom, isEnabled, onZoomHandler]
  );

  const zoomPanHandler = useMemo(
    () => getZoomPanHandler(userOperationMode, scaleExtent, handleZoom),
    [handleZoom, scaleExtent, userOperationMode]
  );

  useEffect(() => {
    const target = zoomPanHandlerRef.current;
    if (!target) return;

    if (userOperationMode === 'slide') {
      // NOTE:
      //
      // 1. trackpadのスクロールによるpanning
      // 2. trackpadのピンチによるzooming
      // 1が実装されていなかったため、d3-zoomのデフォルトの挙動を上書き
      //
      // panningはmiroと同様、スワイプしたのと逆の報告に移動する（逆にする場合は-1を掛ける）
      //
      // TODO:
      //
      // ・マウスホイールによるzoomがpanningと判定されている
      // ・Ctrlキーを押した状態だとマウスホイールでzoomするが、拡大・縮小率をもう少し抑えたい（d3内部でCtrlキー押下の場合に倍率を上げている）
      const zoomOrPan = (eventEx: WheelEventEx) => {
        // ブラウザ自体のzoomが発火しないようにする
        eventEx.preventDefault();

        const currentTransform = (eventEx.currentTarget as EventTargetEx).__zoom;

        if (eventEx.ctrlKey) {
          // NOTE:
          //   トラックパッドでピンチのジェスチャを行うと、event.ctrlKeyがtrueになる(非直感的だがブラウザの実装)
          //   参考: https://medium.com/@auchenberg/detecting-multi-touch-trackpad-gestures-in-javascript-a2505babb10e
          const mousePointer = pointer(eventEx);
          const calculatedScale = currentTransform.k - currentTransform.k * eventEx.deltaY * 0.01; // 現在の倍率から変化量を計算
          const adjustedScale = Math.max(scaleExtent.min, Math.min(scaleExtent.max, calculatedScale));

          const diff = adjustedScale - currentTransform.k;
          if (diff === 0) {
            // zoom scale not changed
            return;
          }

          const invertedMousePointer = [mousePointer, currentTransform.invert(mousePointer)];

          const translatedTransform = translate(
            scaled(currentTransform, adjustedScale, scaleExtent),
            invertedMousePointer[0],
            invertedMousePointer[1]
          );
          const constrainedTransform = constrain(translatedTransform, getExtent(viewBox));

          // D3ランタイムは4番目の引数でeventを受け取るが、@types/d3-zoomの型定義に含まれていない
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (zoomPanHandler.transform as any)(select(target), constrainedTransform, mousePointer, eventEx);
        } else {
          // panning
          // 移動比率を倍率に応じて変化させる（拡大するほど遅くする）
          const panFactor = 0.85 / currentTransform.k;

          const dX = eventEx.wheelDeltaX * panFactor;
          const dY = eventEx.wheelDeltaY * panFactor;

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (zoomPanHandler.translateBy as any)(select(target), dX, dY, eventEx);
        }
      };

      select(target).call(zoomPanHandler).on('wheel.zoom', zoomOrPan);
    } else {
      // userOperationMode: map
      select(target).call(zoomPanHandler);
    }

    return () => {
      select(target).on('zoom', null);
      select(target).on('wheel.zoom', null);
    };
  }, [scaleExtent, scaleExtent.max, scaleExtent.min, userOperationMode, viewBox, zoomPanHandler, zoomPanHandlerRef]);

  const resetZoom = (centerPosition: Point, scale: number, duration?: number) => {
    if (!zoomPanHandlerRef.current) {
      return;
    }

    cancelPendingZoom();
    const selection = select(zoomPanHandlerRef.current);
    const newTransform = zoomIdentity
      .translate(-1 * centerPosition.x * scale, -1 * centerPosition.y * scale)
      .scale(scale);

    // D3 はホイール操作中の sourceEvent を再利用するため、プログラムからの変更を明示する。
    const transformWithSourceEvent = zoomPanHandler.transform as (
      ...args: [...Parameters<typeof zoomPanHandler.transform>, Event]
    ) => void;

    if (duration && duration > 0) {
      // アニメーション付きでズーム/パンを変更
      selection
        .transition()
        .duration(duration)
        .ease(easeQuadOut)
        .call(transformWithSourceEvent, newTransform, undefined, programmaticZoomEvent);
    } else {
      // 即座に変更（従来の動作）
      selection.call(transformWithSourceEvent, newTransform, undefined, programmaticZoomEvent);
    }
  };

  const zoom = (scale: number) => {
    if (!zoomPanHandlerRef.current) {
      return;
    }

    const scaleByWithSourceEvent = zoomPanHandler.scaleBy as (
      ...args: [...Parameters<typeof zoomPanHandler.scaleBy>, Event]
    ) => void;
    select(zoomPanHandlerRef.current).call(scaleByWithSourceEvent, scale, undefined, programmaticZoomEvent);
  };

  return {
    resetZoom,
    zoom,
  };
};

/**
 * ユーザー操作時にフォローを解除する
 * プログラム的なzoom変更（sourceEvent === null）の場合は解除しない
 */
const stopFollowingOnUserInteraction = (event: { sourceEvent: Event | null }): void => {
  // プログラム的な変更（resetZoom, フォロー同期など）の場合はスキップ
  if (!event.sourceEvent || event.sourceEvent === programmaticZoomEvent) return;

  const store = getDefaultStore();
  const followingUserId = store.get(followingUserIdAtom);

  if (followingUserId) {
    store.set(followingUserIdAtom, null);
  }
};

const getZoomPanHandler = (
  userOperationMode: UserOperationModes,
  scaleExtent: ScaleExtent,
  onZoom: (t: ZoomTransform, sourceEvent: Event | null) => void
): ZoomBehavior<SVGGElement, unknown> => {
  //
  if (userOperationMode === 'map') {
    return Zoom<SVGGElement, unknown>()
      .on('zoom', (event) => {
        stopFollowingOnUserInteraction(event);
        onZoom(event.transform, event.sourceEvent);
      })
      .scaleExtent([scaleExtent.min, scaleExtent.max]);
  } else {
    // userOperationMode: slide
    return Zoom<SVGGElement, unknown>()
      .filter((event: MouseEvent | WheelEvent) => {
        if (event instanceof MouseEvent && event.buttons === MouseEventButton.SecondaryButton) {
          // スライドモードの場合は右クリック＆ドラッグによるpanningを実施
          return true;
        }
        if (event instanceof WheelEvent && event.buttons === MouseEventButton.NoButton) {
          // ホイールによるzoomも許可
          return true;
        }
        return false;
      })
      .on('zoom', (event) => {
        const source = event.sourceEvent;

        if (source && source.buttons === MouseEventButton.SecondaryButton) {
          // 右クリックでコンテキストメニューを出さないようにする
          source.preventDefault();
        }
        stopFollowingOnUserInteraction(event);
        onZoom(event.transform, event.sourceEvent);
      })
      .scaleExtent([scaleExtent.min, scaleExtent.max]);
  }
};
